import React from 'react';
import { BuildIcon } from 'assests/GmsIcons';

export interface YourBuildWidgetConfig {
    showTitle: boolean;
    title: string;
    showShareButton: boolean;
    shareButtonConfig: {
        showText: boolean;
        buttonLabel: string;
        showIcon: boolean;
    };
    showDescription: boolean;
    description: string;
    showAddToCartButton: boolean;
    addToCartButtonConfig: {
        showText: boolean;
        buttonLabel: string;
        showIcon: boolean;
    };
}

interface YourBuildWidgetProps {
    id?: string;
    config?: YourBuildWidgetConfig;
    widgetId?: string;
    isSelected?: boolean;
    onContentUpdate?: (widgetId: string, content: string) => void;
}

const defaultConfig: YourBuildWidgetConfig = {
    showTitle: true,
    title: 'Your Build',
    showShareButton: true,
    shareButtonConfig: {
        showText: true,
        buttonLabel: 'Share',
        showIcon: false
    },
    showDescription: true,
    description: 'Disclaimer: Compatio provides product configurations and recommendations as a guide.',
    showAddToCartButton: true,
    addToCartButtonConfig: {
        showText: true,
        buttonLabel: 'Add to Cart',
        showIcon: false
    }
};

export const YourBuildWidget: React.FC<YourBuildWidgetProps> = ({
    id,
    config = defaultConfig,
    widgetId,
    isSelected = false,
    onContentUpdate
}) => {
    const baseId = id || 'your-build-widget';
    const mergedConfig = { ...defaultConfig, ...config };

    const backgroundClass = isSelected ? 'tw-bg-transparent' : 'tw-bg-gray-100';

    return (
        <div id={baseId} data-testid={baseId} className={`tw-w-full tw-h-full tw-flex tw-flex-col tw-p-4 ${backgroundClass}`}>
            {/* Header Section */}
            <div id={`${baseId}-header`} className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                {mergedConfig.showTitle && (
                    <div id={`${baseId}-title-section`} className="tw-flex tw-items-center tw-gap-2">
                        <BuildIcon />
                        <h2 id={`${baseId}-title`} className="tw-text-sm tw-font-semibold tw-text-[#172B4D]">
                            {mergedConfig.title}
                        </h2>
                    </div>
                )}

                {mergedConfig.showShareButton && (
                    <button
                        id={`${baseId}-share-btn`}
                        data-testid={`${baseId}-share-btn`}
                        className="tw-px-4 tw-py-1 tw-border tw-border-[#6D788C] tw-bg-[#C2C2C2] tw-text-[#172B4D] tw-text-sm tw-flex tw-items-center tw-gap-2"
                        disabled={true}
                    >
                        {mergedConfig.shareButtonConfig.showIcon && (
                            <ShareIcon className="tw-w-4 tw-h-4" />
                        )}
                        {mergedConfig.shareButtonConfig.showText && mergedConfig.shareButtonConfig.buttonLabel}
                    </button>
                )}
            </div>

            {/* Build Content Placeholder */}
            <div id={`${baseId}-content-placeholder`} className="tw-flex-1 tw-min-h-[60px]">

            </div>

            {/* Description Section */}
            {mergedConfig.showDescription && mergedConfig.description && (
                <div id={`${baseId}-description-section`} className="tw-mb-4 tw-p-3">
                    <div
                        id={`${baseId}-description`}
                        className="tw-text-[#172B4D] tw-text-[11px]"
                        dangerouslySetInnerHTML={{ __html: mergedConfig.description }}
                    />
                </div>
            )}

            {/* Add to Cart Button */}
            {mergedConfig.showAddToCartButton && (
                <div id={`${baseId}-add-to-cart-section`} className="tw-flex tw-justify-center">
                    <button
                        id={`${baseId}-add-to-cart-btn`}
                        data-testid={`${baseId}-add-to-cart-btn`}
                        className="tw-px-6 tw-py-2 tw-border tw-border-[#6D788C] tw-bg-[#C2C2C2] tw-text-[#172B4D] tw-text-sm tw-flex tw-items-center tw-gap-2"
                        disabled={true}
                    >
                        {mergedConfig.addToCartButtonConfig.showIcon && (
                            <CartIcon className="tw-w-4 tw-h-4" />
                        )}
                        {mergedConfig.addToCartButtonConfig.showText && mergedConfig.addToCartButtonConfig.buttonLabel}
                    </button>
                </div>
            )}
        </div>
    );
};

export default YourBuildWidget;

const ShareIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 4.026a3 3 0 10-4.026 4.026 3 3 0 004.026-4.026zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
        />
    </svg>
);

const CartIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" 
        />
    </svg>
);
